import { initEngine, setVolume } from './audio-engine';
import { play, sfxHit, sfxKill, sfxShoot } from './audio-kit';
import { noteHit, noteKill } from './audio-density';
/* WebAudio 即時合成，無音檔。這是三個接縫之一：headless 執行時換成 no-op 版本。

   ## 第二版（2026-08-05）

   `SFX.*` 仍然是全專案唯一的入口（模擬層一行都不用改），但**主砲／命中／
   擊殺三個高頻音改走 `audio-engine.ts` + `audio-kit.ts`**：分類匯流排、
   發聲上限、距離衰減與左右聲相、每發變化。跟武器特效的「風格檔」同一個形狀。

   下面那一整套 `tone`/`noise` 是**第一版**，其餘十三個音還在用它，
   同時它也是 A/B 的對照組（`SFX.v2(false)` 切回去）。
   等三個高頻音定調，再照同一個模式把其餘的搬過來。 */

/* ---------- 音效 ---------- */
export var SFX = (function () {
  var ctx = null, master = null, noiseBuf = null, muted = false, last = {};
  /* 預設走第二版。第一版留著只為了 A/B（`SFX.v2(false)`）。 */
  var V2 = true;
  function init() {
    if (ctx) return;
    // headless（Node）沒有 window，整個音效層靜默停用 —— 這是接縫的 no-op 分支
    if (typeof window === 'undefined') return;
    var AC = window.AudioContext || window.webkitAudioContext; if (!AC) return;
    initEngine();               // 第二版的引擎（匯流排、發聲上限、距離、每發變化）
    ctx = new AC();
    master = ctx.createGain(); master.gain.value = 0.22; master.connect(ctx.destination);
    noiseBuf = ctx.createBuffer(1, ctx.sampleRate * 0.6, ctx.sampleRate);
    var d = noiseBuf.getChannelData(0);
    for (var i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  }
  function gate(k, ms) { var t = performance.now(); if (last[k] && t - last[k] < ms) return false; last[k] = t; return true; }
  function tone(f0, f1, dur, type?, vol?) {
    if (!ctx || muted) return;
    var o = ctx.createOscillator(), g = ctx.createGain();
    o.type = type || 'square';
    o.frequency.setValueAtTime(f0, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(Math.max(20, f1), ctx.currentTime + dur);
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0008, ctx.currentTime + dur);
    o.connect(g); g.connect(master); o.start(); o.stop(ctx.currentTime + dur + 0.02);
  }
  function noise(dur, vol, f0, f1) {
    if (!ctx || muted) return;
    var s = ctx.createBufferSource(); s.buffer = noiseBuf;
    var bp = ctx.createBiquadFilter(); bp.type = 'lowpass';
    bp.frequency.setValueAtTime(f0, ctx.currentTime);
    bp.frequency.exponentialRampToValueAtTime(Math.max(60, f1), ctx.currentTime + dur);
    var g = ctx.createGain();
    g.gain.setValueAtTime(vol, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0008, ctx.currentTime + dur);
    s.connect(bp); bp.connect(g); g.connect(master); s.start(); s.stop(ctx.currentTime + dur + 0.02);
  }
  return {
    init: init,
    toggle: function () { muted = !muted; if (master) master.gain.value = muted ? 0 : 0.22; return muted; },
    /** A/B 開關：`false` 切回第一版的嗶聲。**沒有對照就證明不了新的比較好。** */
    v2: function (on) { V2 = !!on; return V2; },
    setVolume: setVolume,
    shoot: function (x?, z?) {
      if (!gate('s', 45)) return;
      if (V2) return sfxShoot({ x: x, z: z });
      tone(760, 300, 0.07, 'square', 0.055);
    },
    /** `step` = 連段升階（0 起跳）。第一版的嗶聲也跟著抬一點，
        不然關掉 v2 之後連段的節奏會整個不見。 */
    slash: function (x?, z?, step?) {
      if (!gate('s', 60)) return;
      if (V2 && play('slash', { x: x, z: z, step: step })) return;
      var k = 1 + (step || 0) * 0.12;
      noise(0.12, 0.13 * k, 4200, 900); tone(900 * k, 420, 0.09, 'triangle', 0.05 * k);
    },
    /* 命中與擊殺走**密度層**（`audio-density.ts`）：時間窗內的事件聚合成
       一個音，而不是靠時間節流隨機丟棄。`imp` = Boss／精英／當前鎖定目標，
       那一隻不聚合、立刻響。密度層關掉（A/B）時退回下面的 gate 路徑。
       沒有座標的呼叫（測試台的裸 `SFX.hit()`）也走 gate 路徑 —— 聚合需要位置。 */
    hit: function (x?, z?, imp?) {
      if (V2 && x !== undefined && z !== undefined && noteHit(x, z, imp)) return;
      if (!gate('h', 30)) return;
      if (V2) return sfxHit({ x: x, z: z, imp: imp });
      noise(0.05, 0.09, 2600, 700);
    },
    kill: function (x?, z?, imp?) {
      if (V2 && x !== undefined && z !== undefined && noteKill(x, z, imp)) return;
      if (!gate('k', 35)) return;
      if (V2) return sfxKill({ x: x, z: z, imp: imp });
      noise(0.20, 0.20, 1500, 120); tone(200, 60, 0.16, 'sawtooth', 0.06);
    },
    missile: function (x?, z?) {
      if (V2 && play('missile', { x: x, z: z })) return;
      tone(180, 900, 0.22, 'sawtooth', 0.10); noise(0.18, 0.12, 900, 200);
    },
    beam: function (x?, z?) {
      if (V2 && play('beam', { x: x, z: z })) return;
      tone(90, 1400, 0.5, 'sawtooth', 0.14); noise(1.6, 0.10, 3000, 900);
    },
    dash: function () {
      if (V2 && play('dash')) return;
      noise(0.16, 0.13, 3200, 400);
    },
    hurt: function () {
      if (V2 && play('hurt')) return;
      tone(300, 70, 0.22, 'square', 0.13); noise(0.16, 0.14, 800, 120);
    },
    level: function () {
      if (V2 && play('level')) return;
      tone(520, 1050, 0.16, 'triangle', 0.13);
      setTimeout(function () { tone(780, 1560, 0.20, 'triangle', 0.11); }, 90);
    },
    /* 這一輪有「拿了就湊齊進化」的選項。三連上行，跟 level 的兩連分得開 ——
       `03-in-run-progression.md` 說進化前置的標記「是最重要的引導」，
       而只做視覺的話玩家的眼睛還在看敵人，那一瞬間會漏掉。 */
    preEvo: function () {
      if (V2 && play('preEvo')) return;
      tone(660, 1320, 0.13, 'triangle', 0.11);
      setTimeout(function () { tone(880, 1760, 0.13, 'triangle', 0.11); }, 80);
      setTimeout(function () { tone(1320, 2640, 0.28, 'triangle', 0.12); }, 168);
    },
    boom: function (x?, z?) {
      if (V2 && play('boom', { x: x, z: z })) return;
      noise(0.55, 0.30, 1200, 60); tone(140, 40, 0.45, 'sawtooth', 0.10);
    },
    portal: function () {
      if (V2 && play('portal')) return;
      tone(300, 1500, 0.7, 'sine', 0.12); tone(150, 750, 0.7, 'triangle', 0.08);
    },
    revive: function () {
      if (V2 && play('revive')) return;
      tone(120, 900, 0.6, 'sawtooth', 0.16); noise(0.7, 0.2, 2000, 200);
    },
    alarm: function () {
      if (V2 && play('alarm')) return;
      tone(440, 440, 0.18, 'square', 0.10);
      setTimeout(function () { tone(330, 330, 0.26, 'square', 0.10); }, 200);
    }
  };
})();
